import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useStore from "../store/useStore";
import { FaSpinner } from "react-icons/fa";

/**
 * Subscription Mandate Return URL handler
 * Route: /subscription/mandate/return?sub_id=123
 *
 * Cashfree redirects here after the UPI AutoPay mandate flow (success or failure).
 * We poll the subscription status to get the real mandate result and show the screen.
 */
const SubscriptionMandateReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifySubscriptionStatus, fetchSubscriptionDetail } = useStore();

  const subId = searchParams.get("sub_id");
  const [status, setStatus] = useState("checking"); // checking | active | pending | failed
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!subId) {
      navigate("/account/subscriptions");
      return;
    }

    const verify = async () => {
      try {
        const resolvedStatus = await verifySubscriptionStatus(subId);
        const sub = await fetchSubscriptionDetail(subId);
        setSubscription(sub);
        setStatus(
          resolvedStatus === "active"
            ? "active"
            : resolvedStatus === "pending_mandate"
            ? "pending"
            : "failed",
        );
      } catch {
        setStatus("failed");
      }
    };

    verify();
  }, [subId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Checking ── */
  if (status === "checking") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50">
        <FaSpinner className="h-10 w-10 animate-spin text-primary-600" />
        <p className="text-sm font-semibold text-gray-500">Verifying your mandate…</p>
      </div>
    );
  }

  /* ── Active / Approved ── */
  if (status === "active") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-primary-50 to-white px-4">
        <div className="w-full max-w-md text-center">
          <div
            className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full shadow-xl"
            style={{
              background: "linear-gradient(135deg, #16a34a, #15803d)",
              boxShadow: "0 20px 40px rgba(22,163,74,0.35)",
            }}
          >
            <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Mandate Approved! 🎉</h1>
          <p className="mb-1 text-gray-500">Your UPI AutoPay mandate has been authorised.</p>
          {subscription && (
            <p className="mb-1 text-sm font-semibold text-gray-700">
              {subscription.product_name} — {subscription.quantity} {subscription.quantity_unit} /{" "}
              {subscription.frequency}
            </p>
          )}
          <p className="mb-8 text-sm text-gray-400">
            Daily deliveries start from{" "}
            <span className="font-bold text-gray-700">
              {subscription?.next_delivery
                ? new Date(subscription.next_delivery).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "tomorrow"}
            </span>
            .
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => navigate("/account/subscriptions")}
              className="flex-1 rounded-2xl border-2 border-primary-600 py-3.5 text-sm font-bold text-primary-600 hover:bg-primary-50"
            >
              View Subscriptions
            </button>
            <button
              onClick={() => navigate("/products")}
              className="flex-1 rounded-2xl bg-primary-600 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-primary-700"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Pending ── */
  if (status === "pending") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-amber-50">
            <svg className="h-12 w-12 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Mandate Under Review ⏳</h1>
          <p className="mb-2 text-gray-500">
            Your UPI mandate is being processed by your bank. This can take a few minutes.
          </p>
          <p className="mb-8 text-sm text-gray-400">
            Your subscription will activate automatically once the mandate is approved.
          </p>
          <button
            onClick={() => navigate("/account/subscriptions")}
            className="w-full rounded-2xl bg-primary-600 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-primary-700"
          >
            Go to My Subscriptions
          </button>
        </div>
      </div>
    );
  }

  /* ── Failed ── */
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
          <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Mandate Failed</h1>
        <p className="mb-2 text-gray-500">
          The UPI mandate was not approved. Your subscription is not yet active.
        </p>
        <p className="mb-8 text-sm text-gray-400">
          You can retry from My Subscriptions with a different payment method.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={() => navigate("/account/subscriptions")}
            className="flex-1 rounded-2xl bg-primary-600 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-primary-700"
          >
            Retry / View Subscriptions
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex-1 rounded-2xl border-2 border-gray-200 py-3.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionMandateReturn;
